import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import { getUrl } from "aws-amplify/storage";

import FirearmDetailSections from "./AccordionDetailsContent";
import { openFirearmInNewTab, copyFirearmLink } from "../utils/firearmLink";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  DATA_BAR_HEIGHT,
  TOOLBAR_STRIP_HEIGHT,
  DETAIL_INLINE_BREAKPOINT,
} from "../styles/layoutSizes";
import logoDark from "../assets/logo_dark.png";

const PANE_HEADER_MIN_HEIGHT = DATA_BAR_HEIGHT + TOOLBAR_STRIP_HEIGHT;

const EmptyIcon = styled("img")({
  width: 72,
  height: "auto",
  filter: "grayscale(1) brightness(1.4) opacity(0.45)",
});

const Pane = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  minHeight: "100%",
  height: "100%",
  backgroundColor: theme.palette.background.paper,
}));

const PaneHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  paddingInline: theme.spacing(2.5),
  minHeight: PANE_HEADER_MIN_HEIGHT,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ActionsGroup = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  flexShrink: 0,
}));

const FloatingActions = styled("div")(({ theme }) => ({
  position: "absolute",
  top: PANE_HEADER_MIN_HEIGHT + 8,
  right: theme.spacing(1.5),
  zIndex: 50,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  padding: theme.spacing(0.25),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
}));

const TitleStack = styled("div")({
  flex: 1,
  minWidth: 0,
});

const PaneBody = styled("div")(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(0, 2.5, 3, 2.5),
}));

const EmptyState = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  flex: 1,
  width: "100%",
  padding: theme.spacing(6, 4),
  gap: theme.spacing(1.25),
  color: theme.palette.text.secondary,
}));

function FirearmDetailPane({ result, onClose }) {
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const isNarrow = useMediaQuery(`(max-width: ${DETAIL_INLINE_BREAKPOINT - 1}px)`);
  const paneBodyRef = useRef(null);
  const frn = result?.frn?.raw;

  useEffect(() => {
    if (paneBodyRef.current) {
      paneBodyRef.current.scrollTop = 0;
    }
  }, [frn]);

  const showMessage = (m) => {
    setSnackMessage(m);
    setSnackOpen(true);
  };
  const handleSnackClose = (_e, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  if (!result) {
    return (
      <Pane>
        <EmptyState>
          <EmptyIcon src={logoDark} alt="" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            No firearm selected
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
            Select a result from the list to view its full details, calibre table, notes, and download the original PDF.
          </Typography>
        </EmptyState>
      </Pane>
    );
  }

  const vd = result.valid_date.raw;
  const s3Key = `${vd}/frn-${frn}_valid-${vd}.pdf`;

  async function downloadPDF() {
    const path = `public/${s3Key}`;
    try {
      const { url } = await getUrl({
        path,
        options: { expiresIn: 180, validateObjectExistence: true },
      });
      const dummyLink = document.createElement("a");
      dummyLink.href = url.toString();
      document.body.appendChild(dummyLink);
      dummyLink.click();
      document.body.removeChild(dummyLink);
    } catch (e) {
      console.error("PDF download failed for", path, e);
      showMessage("PDF download failed");
    }
  }

  function handleOpenInNew() {
    openFirearmInNewTab(result);
  }

  async function handleCopyLink() {
    try {
      await copyFirearmLink(result);
      showMessage("Link copied");
    } catch {
      showMessage("Could not copy link");
    }
  }

  return (
    <Pane>
      <PaneHeader>
        <TitleStack>
          <Typography
            component="h2"
            sx={{ lineHeight: 1.3, fontSize: "1.125rem", wordBreak: "break-word" }}
          >
            <Box component="span" sx={{ fontWeight: 600, color: "text.secondary", mr: 0.5 }}>
              Model:
            </Box>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {result.model.raw}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: "0.9375rem", mt: 0.25 }}>
            <Box component="span" sx={{ fontWeight: 600, color: "text.secondary", mr: 0.5 }}>
              Make:
            </Box>
            <Box component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
              {result.make.raw}
            </Box>
          </Typography>
        </TitleStack>
        <ActionsGroup>
          {!isNarrow && (
            <>
              <Tooltip title="Copy link">
                <IconButton size="small" onClick={handleCopyLink}>
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton size="small" onClick={handleOpenInNew}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download PDF">
                <IconButton size="small" onClick={downloadPDF}>
                  <GetAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ActionsGroup>
      </PaneHeader>
      <PaneBody ref={paneBodyRef}>
        <FirearmDetailSections result={result} />
      </PaneBody>
      {isNarrow && (
        <FloatingActions>
          <Tooltip title="Copy link" placement="left">
            <IconButton size="small" onClick={handleCopyLink}>
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in new tab" placement="left">
            <IconButton size="small" onClick={handleOpenInNew}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download PDF" placement="left">
            <IconButton size="small" onClick={downloadPDF}>
              <GetAppIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </FloatingActions>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        message={snackMessage}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleSnackClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Pane>
  );
}

FirearmDetailPane.propTypes = {
  result: PropTypes.object,
  onClose: PropTypes.func,
};

export default FirearmDetailPane;
